import Vue from 'vue'
import { HttpClient } from '@/components/shared/http-client'

const FERRO_PRIMARY_COLOR = '#0b333b'
const FERRO_SECONDARY_COLOR = '#c16e3a'
const FERRO_WARNING_COLOR = '#d7b039'
const FERRO_ACCENT_COLOR = '#d6c1c8'
const FERRO_OTHER_ACCENT_COLOR = '#b8848e'
const FERRO_SUCCESS_COLOR = '#336666'

// load the subDomain from the URL
const [subDomain] = window.location.hostname.split('.')

const SUBDOMAIN = Object.freeze({
  APP: 'app',
  WWW: 'www',
  LOCALHOST: 'localhost',
  HMA: 'hma',
  UNICO: 'unicogroup',
  UNITEL: 'unitel',
  INSURICA: 'insurica',
  INSURICA_DIRECT: 'insuricadirect',
  BANKING: 'banking',
  BRP: 'brp',
  HYLANT: 'hylant',
  IMA: 'ima',
  VIRTUS: 'virtus',
  SWINGLE_COLLINS: 'swinglecollins',
  CRS_INSURANCE: 'crs',
  HORTON: 'horton',
  KA: 'krausanderson',
  HILB: 'hilb',
  ALPS: 'alps',
})

const whiteLabels = {
  [SUBDOMAIN.APP]: {
    subdomain: SUBDOMAIN.APP,
    logo: `ferro-logo-white-thick.svg`,
    type: 'svg',
    styleClass: 'ferro-logo-svg',
    isClientSubdomain: false,
  },
  [SUBDOMAIN.WWW]: {
    subdomain: SUBDOMAIN.WWW,
    logo: `ferro-logo-white-thick.svg`,
    type: 'svg',
    styleClass: 'ferro-logo-svg',
    isClientSubdomain: false,
  },
  [SUBDOMAIN.HMA]: {
    subdomain: SUBDOMAIN.HMA,
    logo: `hma-logo.png`,
    type: 'generic',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.UNICO]: {
    subdomain: SUBDOMAIN.UNICO,
    logo: `unico-white-1.png`,
    type: 'generic',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.UNITEL]: {
    subdomain: SUBDOMAIN.UNITEL,
    logo: `unitel-white.png`,
    type: 'generic',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.INSURICA]: {
    subdomain: SUBDOMAIN.INSURICA,
    logo: `logos/insurica-direct-white.png`,
    type: 'generic',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.INSURICA_DIRECT]: {
    subdomain: SUBDOMAIN.INSURICA_DIRECT,
    logo: `logos/insurica-direct-white.png`,
    type: 'generic',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.ALPS]: {
    subdomain: SUBDOMAIN.ALPS,
    logo: `logos/alps.png`,
    type: 'generic',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.BRP]: {
    subdomain: SUBDOMAIN.BRP,
    logo: `brp-logo.svg`,
    type: 'svg',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.BANKING]: {
    subdomain: SUBDOMAIN.BANKING,
    logo: `ferro-bank-logo.svg`,
    type: 'svg',
    isClientSubdomain: false,
  },
  [SUBDOMAIN.HYLANT]: {
    subdomain: SUBDOMAIN.HYLANT,
    logo: `hylant.svg`,
    type: 'svg',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.IMA]: {
    subdomain: SUBDOMAIN.IMA,
    logo: `ima.webp`,
    type: 'generic',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.VIRTUS]: {
    subdomain: SUBDOMAIN.VIRTUS,
    logo: `logos/virtus-logo.webp`,
    type: 'generic',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.CRS_INSURANCE]: {
    subdomain: SUBDOMAIN.CRS_INSURANCE,
    logo: `logos/crs-logo.svg`,
    type: 'svg',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.SWINGLE_COLLINS]: {
    subdomain: SUBDOMAIN.SWINGLE_COLLINS,
    logo: `logos/swingle-collins-logo.png`,
    type: 'generic',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.HORTON]: {
    subdomain: SUBDOMAIN.HORTON,
    logo: `logos/horton.svg`,
    type: 'svg',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.KA]: {
    subdomain: SUBDOMAIN.KA,
    logo: `logos/ka-logo.png`,
    type: 'generic',
    isClientSubdomain: true,
  },
  [SUBDOMAIN.HILB]: {
    subdomain: SUBDOMAIN.HILB,
    logo: `logos/hilb-white.png`,
    type: 'generic',
    isClientSubdomain: true,
  },
}

// Replace the localhost theme here with whatever customer you want to test with
whiteLabels[SUBDOMAIN.LOCALHOST] = whiteLabels[SUBDOMAIN.APP]

function setColors({ primaryColor, secondaryColor, warningColor, accentColor, otherAccent, successColor }) {
  document.body.style.setProperty('--color-primary', primaryColor || FERRO_PRIMARY_COLOR)
  Vue.prototype.$vuetify.theme.themes.light.primary = primaryColor || FERRO_PRIMARY_COLOR
  document.body.style.setProperty('--color-secondary', secondaryColor || FERRO_SECONDARY_COLOR)
  Vue.prototype.$vuetify.theme.themes.light.secondary = secondaryColor || FERRO_SECONDARY_COLOR
  document.body.style.setProperty('--color-warning', warningColor || FERRO_WARNING_COLOR)
  Vue.prototype.$vuetify.theme.themes.light.warning = warningColor || FERRO_WARNING_COLOR
  document.body.style.setProperty('--color-accent', accentColor || FERRO_ACCENT_COLOR)
  Vue.prototype.$vuetify.theme.themes.light.accent = accentColor || FERRO_ACCENT_COLOR
  document.body.style.setProperty('--color-other-accent', otherAccent || FERRO_OTHER_ACCENT_COLOR)
  Vue.prototype.$vuetify.theme.themes.light.otherAccent = otherAccent || FERRO_OTHER_ACCENT_COLOR
  document.body.style.setProperty('--color-success', successColor || FERRO_SUCCESS_COLOR)
  Vue.prototype.$vuetify.theme.themes.light.success = successColor || FERRO_SUCCESS_COLOR
}

// accountConfiguredSubDomain is the configured subdomain for the brokerage that an account is attached to
function setTheme(accountConfiguredSubDomain) {
  // redirect to the custom sub-domain if they're not already there
  if (accountConfiguredSubDomain != null && accountConfiguredSubDomain !== subDomain) {
    const { isClientSubdomain = false } = whiteLabels[accountConfiguredSubDomain] ?? {}

    if (isClientSubdomain) {
      const redirectTo = new URL(window.location.href)
      redirectTo.hostname = redirectTo.hostname.replace(subDomain, accountConfiguredSubDomain)

      // don't redirect on localhost
      if (subDomain.includes('localhost')) {
        console.log(`Would have redirected to custom subDomain: ${redirectTo.toString()}`)
      } else {
        location.href = redirectTo.toString()
      }
    }
  }

  // When theming, we want to respect the subdomain account-broker configuration first and foremost.
  // But some routes do not have a broker or account in context, so we want to use the subdomain in that case
  // (ie: login flow, signing flows)
  // Finally, fallthrough to just using the default app subdomain
  const customSubDomain = accountConfiguredSubDomain || subDomain || SUBDOMAIN.APP
  HttpClient.get(`/open/branding/${customSubDomain}`).then(response => setColors(response.data.colors))
}

const allWhiteLabelOptions = Object.keys(whiteLabels)
export { allWhiteLabelOptions, whiteLabels, subDomain, setTheme }
